import {
  AiScribeJob,
  AiScribeStatus,
} from '../interfaces/aiScribeJob.interface';

/**
 * What the main viewer shows when a user opens a case. Mirrors
 * `CASE_DEFAULT_VIEWS` in document-llm-api, which owns the values.
 */
export const CASE_DEFAULT_VIEWS = [
  'LATEST_DOCUMENT',
  'LATEST_AI_SCRIBE',
] as const;

export type CaseDefaultView = (typeof CASE_DEFAULT_VIEWS)[number];

export const DEFAULT_CASE_DEFAULT_VIEW: CaseDefaultView = 'LATEST_DOCUMENT';

export function isCaseDefaultView(value: unknown): value is CaseDefaultView {
  return (
    typeof value === 'string' &&
    (CASE_DEFAULT_VIEWS as readonly string[]).includes(value)
  );
}

export type CaseOpenTarget<D> =
  | { kind: 'document'; document: D }
  | { kind: 'aiScribe'; aiScribe: AiScribeJob }
  | null;

/**
 * The newest AI scribe by creation time that isn't a draft still being
 * recorded; opening a draft would open the recorder instead of the viewer.
 */
export function latestAiScribe(aiScribes: AiScribeJob[]): AiScribeJob | null {
  let latest: AiScribeJob | null = null;
  for (const job of aiScribes) {
    if (job.status === AiScribeStatus.IN_PROGRESS) continue;
    if (!latest || createdAtMs(job) > createdAtMs(latest)) latest = job;
  }
  return latest;
}

/**
 * Picks what to show when a case is opened. `sortedDocuments` is newest first;
 * each view falls back to the other when the case has none of its kind.
 */
export function pickCaseOpenTarget<D>(
  view: CaseDefaultView,
  sortedDocuments: D[],
  aiScribes: AiScribeJob[],
): CaseOpenTarget<D> {
  const document = sortedDocuments[0] ?? null;
  const aiScribe = latestAiScribe(aiScribes);
  const documentTarget = document
    ? { kind: 'document' as const, document }
    : null;
  const aiScribeTarget = aiScribe
    ? { kind: 'aiScribe' as const, aiScribe }
    : null;
  return view === 'LATEST_AI_SCRIBE'
    ? (aiScribeTarget ?? documentTarget)
    : (documentTarget ?? aiScribeTarget);
}

function createdAtMs(job: AiScribeJob): number {
  return job.createdAt ? new Date(job.createdAt).getTime() : 0;
}
